import type { UIMessage } from 'ai';
import { type UITools } from 'ai';
import { type PlanItemDto } from '@terra/shared-contracts-agent-execution';
import type { ConfirmationMessageType } from './confirmation.messages.types';
import type { BaseDataMessage } from './base-message.types';
import type { ToolCallGenericMessage } from './toolcalls.types';

export type UIMessagePartType = UIMessage['parts'][number]['type'];

export type TerraUIDataParts = {
  customData: DataMessageType;
  confirmationPrompt: ConfirmationMessageType;
};

export type TerraUIMessage = UIMessage<unknown, TerraUIDataParts, UITools>;

export type TerraUIMessagePart = TerraUIMessage['parts'][number];

export type TerraUIMessageDataPart = Extract<
  TerraUIMessagePart,
  { type: typeof TerraUIMessageDataPartName }
>;

// Generic type for TerraUIMessageDataPart with specific data type using Extract
export type TerraUIMessageDataPartWithType<T extends DataMessageTypeEnum> =
  TerraUIMessageDataPart & { data: Extract<DataMessageType, { type: T }> };

/* the data part identified for all data messages */
export const TerraUIMessageDataPartName = 'data-customData';

export enum DataMessageTypeEnum {
  SessionObjective = 'data-sessionObjective',
  Baseline = 'data-baseline',
  Memories = 'data-memories',
  AttackPlan = 'data-attackPlan',
  Todos = 'data-todos',
  CustomData = 'data-customData',
  Mentions = 'data-mentions',
  ConversationAttachments = 'data-conversationAttachments',
  SessionContext = 'data-sessionContext',
  SessionPeerReview = 'data-sessionPeerReview',
  TraceMetadata = 'data-traceMetadata',
  TokenUsage = 'data-tokenUsage',
  Compaction = 'data-compaction',
  // tool calls
  ToolCall = 'data-toolCall',
  ToolCallUpdate = 'data-toolCallUpdate',
  ToolCallStart = 'data-toolCallStart',
  ToolCallComplete = 'data-toolCallComplete',
  ToolCallError = 'data-toolCallError',
  SmokeReasoning = 'data-smokeReasoning',
  BackgroundUpdate = 'data-backgroundUpdate',
  CopilotModeSwitch = 'data-copilotModeSwitch',
  CopilotPlan = 'data-copilotPlan',
  CopilotPlanMissing = 'data-copilotPlanMissing',
  TurnStopped = 'data-turnStopped',
}

export type DataMessageType =
  | AttackPlanMessage
  | SessionObjectiveMessage
  | BaselineDataMessage
  | MemoriesMessage
  | TodosMessage
  | MentionsMessage
  | ConversationAttachmentsMessage
  | SessionContextMessage
  | SessionPeerReviewMessage
  | CustomDataMessage
  | TraceMetadataMessage
  | SmokeReasoningMessage
  | TokenUsageMessage
  | CompactionMessage
  | BackgroundUpdateMessage
  | CopilotModeSwitchMessage
  | CopilotPlanMessage
  | CopilotPlanMissingMessage
  | TurnStoppedMessage
  | ToolCallGenericMessage;

export type ObjectContextKind =
  | 'Finding'
  | 'Signal'
  | 'WebPage'
  | 'EndPoint'
  | 'Trace'
  | 'Skill'
  | 'SourceCode'
  | 'Chat';

export type ObjectContextMarkedField = {
  field: string;
  label: string;
  value: string;
};

export type ObjectContextSelection = {
  field?: string;
  lineStart?: number;
  lineEnd?: number;
  excerpt: string;
};

export type ObjectContextMentionData = {
  objectKind: ObjectContextKind;
  objectId: string;
  objectLabel: string;
  fields?: ObjectContextMarkedField[];
  selection?: ObjectContextSelection;
  rawText: string;
};

export type ExternalAssetKind =
  | 'NETBLOCK'
  | 'DOMAIN'
  | 'HOST'
  | 'GATEWAY'
  | 'NETWORK_SERVICE'
  | 'SOFTWARE'
  | 'STORAGE_SERVICE';

export type ExternalAssetMentionData = {
  assetKind: ExternalAssetKind;
  surfaceId: string;
  label: string;
  subtitle?: string;
  rawText: string;
};

export type ObjectUpdateProposal = {
  type: 'object_update_proposal';
  objectKind: string;
  objectId: string;
  patch: Record<string, unknown>;
  reason: string;
};

export type PortalObjectKind =
  | 'Finding'
  | 'Signal'
  | 'EndPoint'
  | 'WebPage'
  | 'Trace'
  | 'SkillDraft';

export type PortalObjectPresentation = {
  type: 'portal_object_presentation';
  objectKind: PortalObjectKind;
  objectId: string;
  title: string;
  reason?: string;
  endpointUrl?: string;
  skillDraftMode?: 'create' | 'edit';
  skillId?: string;
};

export type VegaLiteVisualization = {
  type: 'vega_lite_visualization';
  title: string;
  description?: string;
  scopeLabel: string;
  spec: Record<string, unknown>;
};

export type CoPilotSessionExportFormat = 'markdown' | 'csv' | 'har';

export type CoPilotSessionExportArtifact = {
  type: 'copilot_session_export';
  format: CoPilotSessionExportFormat;
  filename: string;
  title: string;
  formatLabel: string;
  applicationId: string;
  threadId: string;
  sessionStatus?: 'running' | 'completed' | 'error' | 'aborted';
  requestFilename?: string;
  csvObjectKinds?: Array<
    'Finding' | 'Signal' | 'EndPoint' | 'Trace' | 'WebPage' | 'Skill'
  >;
  csvSources?: Array<'mentioned' | 'created' | 'surfaced'>;
  csvKind?: 'transcript' | 'objects' | 'http_exchanges';
  markdownKind?: 'transcript' | 'objects' | 'session' | 'http_exchanges';
};

export type AdGraphVisualizationNode = {
  id: string;
  kind: string;
  label: string;
  isCrownJewel?: boolean;
  isOwned?: boolean;
};

export type AdGraphVisualizationEdge = {
  sourceId: string;
  targetId: string;
  relationshipType: string;
  isAbusable?: boolean;
};

export type AdGraphVisualization = {
  type: 'ad_graph_visualization';
  title: string;
  description?: string;
  graph: {
    nodes: AdGraphVisualizationNode[];
    edges: AdGraphVisualizationEdge[];
    truncated?: boolean;
    totalCount?: number;
  };
};

export type MentionEntity = {
  entityType:
    | 'Finding'
    | 'Signal'
    | 'WebPage'
    | 'EndPoint'
    | 'NodeName'
    | 'TestingInstruction'
    | 'HttpRequest'
    | 'HttpResponse'
    | 'Trace'
    | 'Interact'
    | 'Skill'
    | 'ObjectContext'
    | 'ExternalAsset';
  entityId: string;
  label: string;
  extraData?: Record<string, unknown>;
};

/**
 * Structured HTTP request/response data for rich display in chat
 * While `extraData` contains this for rich UI rendering,
 * the plain text version is sent to backend for AI processing
 */
export type HttpMentionData = {
  /** HTTP method (GET, POST, etc.) */
  method: string;
  /** Full URL */
  url: string;
  /** HTTP status code (for responses) */
  statusCode?: number;
  /** HTTP status text */
  statusText?: string;
  /** Headers as key-value pairs */
  headers: Record<string, string>;
  /** Request/response body */
  body?: string;
  /** Content-Type of the body */
  contentType?: string;
  /** Plain text representation for backend/AI */
  rawText: string;
  /** Canonical HTTP version when known (e.g. 2.0, HTTP/2.0) */
  httpVersion?: string;
};

export type SkillMentionData = {
  skillDraftId: string;
  skillId?: string;
  mode: 'create' | 'edit';
};

export type FindingMentionData = {
  /** Finding title */
  title: string;
  /** Severity level */
  severity?: string;
  /** Finding category */
  category?: string;
  /** Finding status */
  status?: string;
  /** Short description */
  shortDescription?: string;
  /** CVSS score */
  cvssScore?: string;
  /** CVSS vector */
  cvssVector?: string;
  /** Description */
  description?: string;
  /** Proof of concept */
  poc?: string;
  /** Business impact */
  businessImpact?: string;
  /** Mitigation plan */
  mitigationPlan?: string;
  /** Affected assets (display strings) */
  affectedAssets?: string[];
  /** ID of the signal that originated this finding */
  signalId?: string;
  /** Plain text representation for backend/AI */
  rawText: string;
};

export type InteractMentionData = {
  /** Session ID */
  sessionId: string;
  /** Interaction ID */
  interactionId: string;
  /** Protocol type */
  protocol: 'http' | 'dns' | 'smtp' | 'ftp';
  /** Terra Link domain */
  domain: string;
  /** Timestamp of interaction */
  timestamp: string;
  /** Remote address that triggered the interaction */
  remoteAddress: string;
  /** DNS query type (for DNS interactions) */
  qType?: string;
  /** HTTP method (for HTTP interactions) */
  httpMethod?: string;
  /** HTTP path (for HTTP interactions) */
  httpPath?: string;
  /** HTTP headers (for HTTP interactions) */
  httpHeaders?: Record<string, string>;
  /** HTTP body (for HTTP interactions) */
  httpBody?: string;
  /** Raw request data */
  rawRequest?: string;
  /** Raw response data */
  rawResponse?: string;
  /** Plain text representation for backend/AI */
  rawText: string;
};

export interface MentionsMessage extends BaseDataMessage {
  type: DataMessageTypeEnum.Mentions;
  mentions: MentionEntity[];
  segments?: MessageSegment[];
}

export type TextSegment = { type: 'text'; value: string };
export type MentionSegment = {
  type: 'mention';
  entityId: string;
  entityType: MentionEntity['entityType'];
  label: string;
};
export type MessageSegment = TextSegment | MentionSegment;

export type ConversationAttachmentReference = {
  id: string;
  fileName: string;
  fileType: 'PDF' | 'DOCX' | 'XML' | 'CSV' | 'IMAGE';
  sizeBytes: number;
};

export interface ConversationAttachmentsMessage extends BaseDataMessage {
  type: DataMessageTypeEnum.ConversationAttachments;
  attachments: ConversationAttachmentReference[];
}

export interface CompletionCriteriaItem {
  title: string;
  status: 'met' | 'not met' | 'in progress' | 'not started';
  reason: string;
  evidence?: string;
}

export interface SessionObjectiveMessage extends BaseDataMessage {
  type: DataMessageTypeEnum.SessionObjective;
  sessionObjective?: {
    objective: string;
    completionCriteria: CompletionCriteriaItem[];
    state: 'active' | 'completed';
  };
}

export interface BaselineDataMessage extends BaseDataMessage {
  type: DataMessageTypeEnum.Baseline;
  baselineData?: string;
}

export interface MemoryItem {
  id: string;
  content: string;
  category: 'short-term' | 'long-term';
  timestamp: string;
}

export interface MemoriesMessage extends BaseDataMessage {
  type: DataMessageTypeEnum.Memories;
  memories?: {
    shortTerm: MemoryItem[];
    longTerm: MemoryItem[];
  };
}

export interface AttackPlanMessage extends BaseDataMessage {
  type: DataMessageTypeEnum.AttackPlan;
  plan: PlanItemDto[];
}

export interface CopilotModeSwitchMessage extends BaseDataMessage {
  type: DataMessageTypeEnum.CopilotModeSwitch;
  fromMode: 'agent' | 'plan' | 'ask';
  toMode: 'agent' | 'plan' | 'ask';
  requiresConfirmation: boolean;
  reason?: string;
}

export interface CopilotPlanStep {
  order: number;
  title: string;
  detail?: string;
}

export interface CopilotPlanMessage extends BaseDataMessage {
  type: DataMessageTypeEnum.CopilotPlan;
  title: string;
  summary?: string;
  targetEndpoints?: string[];
  attackTypes?: string[];
  preconditions?: string[];
  expectedHitlGates?: string[];
  reconArtifactPaths?: string[];
  steps: CopilotPlanStep[];
  filePath: string;
}

export interface CopilotPlanMissingMessage extends BaseDataMessage {
  type: DataMessageTypeEnum.CopilotPlanMissing;
  reason?: string;
}

export interface TodoItem {
  id: string;
  content: string;
  state: 'pending' | 'completed' | 'in_progress' | 'cancelled';
}

export interface TodosMessage extends BaseDataMessage {
  type: DataMessageTypeEnum.Todos;
  todos?: TodoItem[];
}

export interface SessionContextMessage extends BaseDataMessage {
  type: DataMessageTypeEnum.SessionContext;
  sessionContext?: string;
}

export interface SessionPeerReviewMessage extends BaseDataMessage {
  type: DataMessageTypeEnum.SessionPeerReview;
  sessionPeerReview?: string;
}

/**
 * Generic custom data message for miscellaneous data that doesn't fit
 * into the specific message types above.
 */
export interface CustomDataMessage extends BaseDataMessage {
  type: DataMessageTypeEnum.CustomData;
  [key: string]: unknown;
}

export interface TraceMetadataMessage extends BaseDataMessage {
  type: DataMessageTypeEnum.TraceMetadata;
  traceId: string;
  threadId?: string;
}

export type SmokeReasoningMessage = BaseDataMessage & {
  type: DataMessageTypeEnum.SmokeReasoning;
  text: string;
};

export type BackgroundUpdateMessage = BaseDataMessage & {
  type: DataMessageTypeEnum.BackgroundUpdate;
  agentType: string;
  toolCallId?: string;
  outcome?: 'stopped';
};

export type TurnStoppedMessage = BaseDataMessage & {
  type: DataMessageTypeEnum.TurnStopped;
};

export const COPILOT_MAIN_AGENT_LABEL = 'copilot::main-agent';
export const COPILOT_SUBAGENT_LABEL_PREFIX = 'subagent::';

export interface TokenUsageBreakdown {
  systemTokens: number;
  toolsTokens: number;
  conversationTokens: number;
}

export interface TokenUsageMessage extends BaseDataMessage {
  type: DataMessageTypeEnum.TokenUsage;
  agent: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  contextWindowTokens?: number;
  reserveTokens?: number;
  effectiveWindowTokens?: number;
  triggerTokens?: number;
  usedFraction?: number;
  compactionCount?: number;
  threadId?: string;
  breakdown?: TokenUsageBreakdown;
}

export type CompactionStatus = 'started' | 'streaming' | 'completed' | 'failed';

export interface CompactionMessage extends BaseDataMessage {
  type: DataMessageTypeEnum.Compaction;
  agent: string;
  status: CompactionStatus;
  threadId?: string;
  tokensBefore?: number;
  tokensAfter?: number;
  durationMs?: number;
  summary?: string;
}
